// ============================================================================
// PacketInboxView — Phase 1
//
// Two tabs:
//   Inbox     = unassigned packet source material (status = INBOX)
//   Completed = packet assigned to a referral    (status = COMPLETED)
//
// Roles & capability matrix (UI-side; CFs enforce server-side):
//   gp_employee:        upload + view + Start New Referral
//   intake_coordinator: upload + view (no Start New Referral in Phase 1)
//   tenant_admin,
//   ops_manager,
//   platform_admin:     view only (operational visibility)
//
// No PHI is rendered. Original filenames live only inside PHI manifests on
// the server.
// ============================================================================

import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { safeUserError } from '../core/safeError.js';
import { Upload, Inbox, CheckSquare, FileText, AlertCircle, Loader2, RotateCcw, FolderOpen, Trash2, Undo2, FilePlus } from 'lucide-react';
import { useAuth } from '../auth/AuthContext.jsx';
import {
  createPacket,
  createPacketDocumentUploadUrl,
  finalizePacketDocumentUpload,
  markPacketDrafting,
  releasePacketDraft,
  deletePreReferralPacket,
  restorePreReferralPacket,
  listPacketDocuments,
  computePacketReadiness,
  getPacketScanReadiness,
  subscribePackets,
  PACKET_STATUS,
  PACKET_PREPARATION_STATUS,
} from '../core/store.packets.js';
import { isGpEmployee, isIntakeCoordinator, canUploadPacketDocuments } from '../core/rbac.js';
import { getSmartLaunchHandoffState, canAddSupportingDocsToEhrPacket } from '../core/smartLaunchGate.js';
import PacketReviewDrawer from './PacketInbox/PacketReviewDrawer.jsx';
import PreparingGateModal from './PacketInbox/PreparingGateModal.jsx';

const TAB = { INBOX: 'inbox', COMPLETED: 'completed', DELETED: 'deleted' };

// Learning-step → tab sync (UI-only). A Packet Inbox learning card may target a
// specific tab via its focusTarget (see roleLearningMaps `packet_inbox_intro`); when
// that step is active, the page selects the matching tab. No data/query/status change.
const TAB_FOCUS_TARGETS = {
  packet_inbox_tab_inbox: TAB.INBOX,
  packet_inbox_tab_completed: TAB.COMPLETED,
  packet_inbox_tab_deleted: TAB.DELETED,
  // packet_snippet card focuses one packet card — it lives on the Inbox tab.
  packet_card: TAB.INBOX,
};

const MIME_TO_TYPE = (mime) => {
  if (!mime) return 'OTHER';
  if (mime === 'application/pdf') return 'PDF';
  if (mime.startsWith('image/')) return 'PHOTO';
  return 'OTHER';
};

const formatTimestamp = (ts) => {
  if (!ts) return '—';
  const d = ts?.toDate ? ts.toDate() : new Date(ts);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString();
};

// Human, PHI-free packet identity: "Jun 1, 6:19 PM" (from createdAt). Used as
// the card title instead of a random ID, which stays as secondary debug text.
const formatDateTime = (ts) => {
  if (!ts) return '—';
  const d = ts?.toDate ? ts.toDate() : new Date(ts);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const shortPacketId = (packetId) => (packetId ? packetId.slice(-7) : '');

// Tailwind text color per scan-readiness tone (non-PHI status only).
const SCAN_TONE_CLASS = {
  ok:    'text-emerald-600',
  busy:  'text-indigo-600',
  warn:  'text-amber-600',
  error: 'text-amber-600',
  muted: 'text-slate-500',
};

const PacketInboxView = ({ onNavigate, activeFocusTarget }) => {
  const { user } = useAuth();
  const role = user?.role;
  const tenantId = user?.tenantId;

  // Single source of truth for packet-upload permission — mirrors the server
  // PACKET_UPLOAD_ROLES gate (functions/packets.js) so the row-level "Add
  // supporting documents" affordance cannot drift from backend enforcement.
  // gp_employee is included, so a GP always sees the upload affordance.
  const canUpload     = canUploadPacketDocuments(role);
  const canReviewDocs = isGpEmployee(role) || isIntakeCoordinator(role); // open + view + type
  const canStartGate  = isGpEmployee(role);

  // Phase 1.5 — packet currently open in the review drawer (null = closed).
  const [reviewPacket, setReviewPacket] = useState(null);

  const [tab, setTab] = useState(TAB.INBOX);
  const [inbox, setInbox]         = useState([]);
  const [completed, setCompleted] = useState([]);
  const [loadingInbox, setLoadingInbox]         = useState(true);
  const [loadingCompleted, setLoadingCompleted] = useState(true);
  const [inboxError, setInboxError]         = useState(null);
  const [completedError, setCompletedError] = useState(null);

  // Upload UI state — per active upload session
  const [activePacketId, setActivePacketId] = useState(null);
  const [uploads, setUploads] = useState([]); // [{ name, status, error? }]
  const [isUploading, setIsUploading] = useState(false);
  const [uploadGlobalError, setUploadGlobalError] = useState(null);
  const fileInputRef = useRef(null);

  // Live subscriptions for both tabs
  useEffect(() => {
    if (!tenantId) return undefined;
    setLoadingInbox(true);
    setInboxError(null);
    const unsub = subscribePackets(tenantId, PACKET_STATUS.INBOX, (rows) => {
      setInbox(rows);
      setLoadingInbox(false);
    });
    return () => unsub && unsub();
  }, [tenantId]);

  useEffect(() => {
    if (!tenantId) return undefined;
    setLoadingCompleted(true);
    setCompletedError(null);
    const unsub = subscribePackets(tenantId, PACKET_STATUS.COMPLETED, (rows) => {
      setCompleted(rows);
      setLoadingCompleted(false);
    });
    return () => unsub && unsub();
  }, [tenantId]);

  // Learning-step tab sync (UI-only). When a learning card targets a tab, select it.
  // Fires only when activeFocusTarget CHANGES (i.e. step entry), so manual tab clicks
  // within the same step are respected and never overridden. No Firestore write, no
  // packet query/status/lifecycle change — pure local tab selection.
  useEffect(() => {
    const target = TAB_FOCUS_TARGETS[activeFocusTarget];
    if (target) setTab(target);
  }, [activeFocusTarget]);

  const handlePickFiles = () => {
    if (!canUpload || isUploading) return;
    setUploadGlobalError(null);
    fileInputRef.current?.click();
  };

  // Start a manual referral — opens the Gate with NO packetId. Reuses the existing
  // manual-Gate fallback (identical to ClinicianDashboard's Start New Referral).
  // GP-only (canStartGate); manual referral is a fallback, not the primary path.
  const handleStartManualReferral = () => {
    if (!canStartGate) return;
    onNavigate('gate');
  };

  // Upload each file into an EXISTING packet (reused by the new-packet flow and the
  // Build 2O EHR "Add supporting documents" flow). Each finalize auto-enqueues OCR
  // server-side (async, non-blocking); a per-file failure is isolated.
  const uploadFilesToPacket = useCallback(async (files, packetId) => {
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      setUploads((prev) => prev.map((u, idx) => idx === i ? { ...u, status: 'uploading' } : u));
      try {
        const type = MIME_TO_TYPE(file.type);
        const { documentId, uploadUrl } = await createPacketDocumentUploadUrl(packetId, {
          mimeType: file.type || 'application/octet-stream',
          originalName: file.name,
          type,
        });
        const putRes = await fetch(uploadUrl, {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': file.type || 'application/octet-stream' },
        });
        if (!putRes.ok) {
          throw new Error('Storage PUT failed with status ' + putRes.status);
        }
        await finalizePacketDocumentUpload(packetId, documentId);
        setUploads((prev) => prev.map((u, idx) => idx === i ? { ...u, status: 'ready' } : u));
      } catch (err) {
        console.error('[PacketInboxView] document upload failed:', file.name, err);
        setUploads((prev) => prev.map((u, idx) => idx === i
          ? { ...u, status: 'failed', error: safeUserError(err) }
          : u));
      }
    }
  }, []);

  const handleFilesChosen = useCallback(async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = ''; // reset chooser
    if (files.length === 0) return;

    setIsUploading(true);
    setUploadGlobalError(null);
    setUploads(files.map((f) => ({ name: f.name, status: 'pending' })));

    let packetId = null;
    try {
      const created = await createPacket();
      packetId = created.packetId;
      setActivePacketId(packetId);
    } catch (err) {
      console.error('[PacketInboxView] createPacket failed:', err);
      setUploadGlobalError(safeUserError(err));
      setIsUploading(false);
      return;
    }

    await uploadFilesToPacket(files, packetId);
    setIsUploading(false);
  }, [uploadFilesToPacket]);

  // ── Build 2O — add supporting documents to an EXISTING EHR-launch packet ──────
  // Reuses the same upload pipeline (createPacketDocumentUploadUrl → PUT →
  // finalize → auto-OCR), but targets the packet's existing packetId instead of
  // creating a new packet. Server enforces upload RBAC + INBOX/DRAFTING_REFERRAL.
  const addDocsInputRef = useRef(null);
  const [addDocsPacketId, setAddDocsPacketId] = useState(null);

  const handleAddDocuments = (packet) => {
    if (!canUpload) return;
    setAddDocsPacketId(packet.packetId);
    setUploadGlobalError(null);
    addDocsInputRef.current?.click();
  };

  const handleAddDocsChosen = useCallback(async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = ''; // reset chooser
    const targetPacketId = addDocsPacketId;
    setAddDocsPacketId(null);
    if (!targetPacketId || files.length === 0) return;

    setIsUploading(true);
    setUploadGlobalError(null);
    setActivePacketId(targetPacketId);
    setUploads(files.map((f) => ({ name: f.name, status: 'pending' })));

    await uploadFilesToPacket(files, targetPacketId);
    setIsUploading(false);
  }, [addDocsPacketId, uploadFilesToPacket]);

  // Packet currently in the "Preparing the Gate" flow (null = none).
  const [preparingPacket, setPreparingPacket] = useState(null);

  // Mark the packet as drafting and open the Gate. Shared by the ready path and
  // the preparation modal's "Continue manually" / ready-resolution.
  const proceedToGate = async (packet) => {
    try {
      await markPacketDrafting(packet.packetId);
      setPreparingPacket(null);
      onNavigate('gate', { packetId: packet.packetId });
    } catch (err) {
      console.error('[PacketInboxView] markPacketDrafting failed:', err);
      setPreparingPacket(null);
      alert(safeUserError(err));
    }
  };

  // Build 2H — EHR-launch packet → existing Gate handoff. Reuses proceedToGate
  // (markPacketDrafting + navigate('gate', { packetId })). EHR-launch packets are
  // candidate-driven (0 documents), so this bypasses the document-readiness gate
  // that handleStartReferral applies to upload-based packets. The Gate then loads
  // the existing EHR Suggestions from the packet's /phi/ehrImport/** candidates.
  const handleContinueSmartLaunch = async (packet) => {
    const sl = getSmartLaunchHandoffState(packet, { canStartGate, callerTenantId: tenantId });
    if (!sl.eligible) {
      alert(sl.blockMessage || 'This launch packet cannot be opened in the Gate.');
      return;
    }
    if (packet.status === PACKET_STATUS.DRAFTING_REFERRAL) {
      onNavigate('gate', { packetId: packet.packetId }); // already drafting — open directly
      return;
    }
    await proceedToGate(packet); // INBOX → markPacketDrafting + open Gate
  };

  const handleStartReferral = async (packet) => {
    if (!canStartGate) return;
    if ((packet.readyDocumentCount || 0) < 1) return;

    // Outcome velocity: if the packet is already prepared, open the Gate
    // immediately — no unnecessary preparation wait. Otherwise show the
    // "Preparing the Gate" screen, which triggers classification/OCR and polls.
    try {
      const { documents } = await listPacketDocuments(packet.packetId);
      const readiness = computePacketReadiness(documents);
      if (readiness === PACKET_PREPARATION_STATUS.READY) {
        await proceedToGate(packet);
        return;
      }
      setPreparingPacket(packet);
    } catch (err) {
      // Readiness check failed — fall back to the preparation modal, which
      // offers manual entry. Manual entry is never permanently blocked.
      console.error('[PacketInboxView] readiness check failed:', err);
      setPreparingPacket(packet);
    }
  };

  const handleReturnToInbox = async (packet) => {
    try {
      await releasePacketDraft(packet.packetId);
    } catch (err) {
      console.error('[PacketInboxView] releasePacketDraft failed:', err);
      alert(safeUserError(err));
    }
  };

  // Soft-delete an unsubmitted packet. The live subscription removes the card
  // once the server flips status → DELETED, so we never remove it optimistically.
  const [deletingId, setDeletingId] = useState(null);
  const handleDeletePacket = async (packet) => {
    if (!canUpload) return;
    const confirmed = window.confirm(
      'Delete this packet from the inbox? Uploaded documents will no longer be available from this packet.',
    );
    if (!confirmed) return;
    setDeletingId(packet.packetId);
    try {
      await deletePreReferralPacket(packet.packetId);
    } catch (err) {
      console.error('[PacketInboxView] deletePreReferralPacket failed:', err);
      const code = err?.code || '';
      const msg = err?.message || '';
      if (code === 'functions/failed-precondition' || /PACKET_ATTACHED_TO_REFERRAL|attached to a referral|already completed/i.test(msg)) {
        alert('This packet is already attached to a referral and cannot be deleted.');
      } else {
        alert('Could not delete packet. Please try again.');
      }
    } finally {
      setDeletingId(null);
    }
  };

  // Inbox view also surfaces stuck DRAFTING_REFERRAL packets owned by the caller
  // — they would normally not appear in the INBOX query, so we subscribe to them
  // separately to provide the visible recovery action.
  const [draftingPackets, setDraftingPackets] = useState([]);
  useEffect(() => {
    if (!tenantId) return undefined;
    const unsub = subscribePackets(tenantId, PACKET_STATUS.DRAFTING_REFERRAL, (rows) => setDraftingPackets(rows));
    return () => unsub && unsub();
  }, [tenantId]);

  // FAILED packets need visible recovery too
  const [failedPackets, setFailedPackets] = useState([]);
  useEffect(() => {
    if (!tenantId) return undefined;
    const unsub = subscribePackets(tenantId, PACKET_STATUS.FAILED, (rows) => setFailedPackets(rows));
    return () => unsub && unsub();
  }, [tenantId]);

  // DELETED packets — soft-deleted, viewable + restorable in the Deleted tab.
  const [deleted, setDeleted] = useState([]);
  const [loadingDeleted, setLoadingDeleted] = useState(true);
  useEffect(() => {
    if (!tenantId) return undefined;
    setLoadingDeleted(true);
    const unsub = subscribePackets(tenantId, PACKET_STATUS.DELETED, (rows) => {
      setDeleted(rows);
      setLoadingDeleted(false);
    });
    return () => unsub && unsub();
  }, [tenantId]);

  // Restore a soft-deleted packet → INBOX. The live subscription moves the card
  // between tabs once the server write succeeds (never optimistically).
  const [restoringId, setRestoringId] = useState(null);
  const handleRestorePacket = async (packet) => {
    if (!canUpload) return;
    const confirmed = window.confirm('Restore this packet to the inbox?');
    if (!confirmed) return;
    setRestoringId(packet.packetId);
    try {
      await restorePreReferralPacket(packet.packetId);
    } catch (err) {
      console.error('[PacketInboxView] restorePreReferralPacket failed:', err);
      const code = err?.code || '';
      const msg = err?.message || '';
      if (code === 'functions/failed-precondition' || /PACKET_ATTACHED_TO_REFERRAL|attached to a referral|completed/i.test(msg)) {
        alert('This packet is already attached to a referral and cannot be restored.');
      } else {
        alert('Could not restore packet. Please try again.');
      }
    } finally {
      setRestoringId(null);
    }
  };

  const inboxBody = useMemo(() => {
    if (loadingInbox) {
      return (
        <div className="p-6 text-slate-500 flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading inbox…
        </div>
      );
    }
    if (inboxError) {
      return (
        <div className="p-6 text-red-700 bg-red-50 border border-red-200 rounded">
          {inboxError}
        </div>
      );
    }
    if (inbox.length === 0 && draftingPackets.length === 0 && failedPackets.length === 0) {
      return (
        <div className="p-10 text-center text-slate-500 border border-dashed border-slate-300 rounded-lg">
          <Inbox className="w-8 h-8 mx-auto mb-2 text-slate-400" />
          <div className="font-medium">No packets waiting.</div>
          <div className="text-sm mt-1">Start from EHR, upload a packet, or create a manual referral.</div>
          <div className="text-xs text-slate-400 mt-3">
            To start from EHR, open the patient chart in your EHR and launch ReferralGate.
          </div>
        </div>
      );
    }
    // packet_snippet learning step highlights the FIRST visible card (UI-only —
    // a data-learning-target anchor + highlight class on one row; no data change).
    const focusFirstCard = activeFocusTarget === 'packet_card';
    const firstCardId = (failedPackets[0]?.id) ?? (draftingPackets[0]?.id) ?? (inbox[0]?.id) ?? null;
    return (
      <ul className="space-y-3">
        {failedPackets.map((p) => (
          <PacketCard
            key={p.id}
            packet={p}
            tone="failed"
            focusAnchor={focusFirstCard && p.id === firstCardId}
            canStartGate={canStartGate}
            canReviewDocs={canReviewDocs}
            canDelete={canUpload}
            deleting={deletingId === p.packetId}
            onReview={() => setReviewPacket(p)}
            onStartReferral={() => handleStartReferral(p)}
            onReturnToInbox={() => handleReturnToInbox(p)}
            onDelete={() => handleDeletePacket(p)}
          />
        ))}
        {draftingPackets.map((p) => (
          <PacketCard
            key={p.id}
            packet={p}
            tone="drafting"
            focusAnchor={focusFirstCard && p.id === firstCardId}
            canStartGate={canStartGate}
            canReviewDocs={canReviewDocs}
            canDelete={canUpload}
            canUpload={canUpload}
            onAddDocuments={() => handleAddDocuments(p)}
            deleting={deletingId === p.packetId}
            onReview={() => setReviewPacket(p)}
            onStartReferral={() => handleStartReferral(p)}
            onContinueSmartLaunch={() => handleContinueSmartLaunch(p)}
            onReturnToInbox={() => handleReturnToInbox(p)}
            onDelete={() => handleDeletePacket(p)}
          />
        ))}
        {inbox.map((p) => (
          <PacketCard
            key={p.id}
            packet={p}
            tone="inbox"
            focusAnchor={focusFirstCard && p.id === firstCardId}
            canStartGate={canStartGate}
            canReviewDocs={canReviewDocs}
            canDelete={canUpload}
            canUpload={canUpload}
            onAddDocuments={() => handleAddDocuments(p)}
            deleting={deletingId === p.packetId}
            onReview={() => setReviewPacket(p)}
            onStartReferral={() => handleStartReferral(p)}
            onContinueSmartLaunch={() => handleContinueSmartLaunch(p)}
            onDelete={() => handleDeletePacket(p)}
          />
        ))}
      </ul>
    );
  }, [loadingInbox, inboxError, inbox, draftingPackets, failedPackets, canStartGate, canReviewDocs, canUpload, deletingId, activeFocusTarget]); // eslint-disable-line react-hooks/exhaustive-deps

  const completedBody = useMemo(() => {
    if (loadingCompleted) {
      return (
        <div className="p-6 text-slate-500 flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading completed…
        </div>
      );
    }
    if (completedError) {
      return (
        <div className="p-6 text-red-700 bg-red-50 border border-red-200 rounded">
          {completedError}
        </div>
      );
    }
    if (completed.length === 0) {
      return (
        <div className="p-10 text-center text-slate-500 border border-dashed border-slate-300 rounded-lg">
          <CheckSquare className="w-8 h-8 mx-auto mb-2 text-slate-400" />
          <div className="font-medium">No assigned packets yet</div>
          <div className="text-sm">Packets appear here once they have been assigned to a referral.</div>
        </div>
      );
    }
    return (
      <ul className="space-y-3">
        {completed.map((p) => (
          <CompletedPacketCard key={p.id} packet={p} />
        ))}
      </ul>
    );
  }, [loadingCompleted, completedError, completed]);

  const deletedBody = useMemo(() => {
    if (loadingDeleted) {
      return (
        <div className="p-6 text-slate-500 flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading deleted…
        </div>
      );
    }
    if (deleted.length === 0) {
      return (
        <div className="p-10 text-center text-slate-500 border border-dashed border-slate-300 rounded-lg">
          <Trash2 className="w-8 h-8 mx-auto mb-2 text-slate-400" />
          <div className="font-medium">No deleted packets</div>
          <div className="text-sm">Deleted packets can be restored to the inbox from here.</div>
        </div>
      );
    }
    return (
      <ul className="space-y-3">
        {deleted.map((p) => (
          <DeletedPacketCard
            key={p.id}
            packet={p}
            canRestore={canUpload}
            restoring={restoringId === p.packetId}
            onRestore={() => handleRestorePacket(p)}
          />
        ))}
      </ul>
    );
  }, [loadingDeleted, deleted, canUpload, restoringId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Referral Packet Inbox</h1>
        <p className="text-sm text-slate-600">
          Upload one or more documents per patient. A packet becomes a referral when a referring provider
          starts one from it.
        </p>
      </div>

      {canUpload && (
        <div className="rounded-lg border border-slate-200 bg-white p-4 flex items-center gap-3">
          <button
            type="button"
            onClick={handlePickFiles}
            disabled={isUploading}
            className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 disabled:opacity-50"
          >
            {isUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {isUploading ? 'Uploading…' : 'Upload Packet'}
          </button>
          {canStartGate && (
            <button
              type="button"
              onClick={handleStartManualReferral}
              className="inline-flex items-center gap-2 px-4 py-2 border border-slate-300 text-slate-700 text-sm font-medium rounded-md hover:bg-slate-50"
              title="Start a referral in the Gate without a packet (fallback when there is no EHR launch or uploaded document)."
            >
              <FilePlus className="w-4 h-4" /> Start Manual Referral
            </button>
          )}
          <span className="text-xs text-slate-500">PDFs and images, up to 25 MB each.</span>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={handleFilesChosen}
            accept="application/pdf,image/*"
          />
          {/* Build 2O — separate chooser that uploads into an existing EHR packet. */}
          <input
            ref={addDocsInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={handleAddDocsChosen}
            accept="application/pdf,image/*"
          />
        </div>
      )}

      {uploadGlobalError && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 mt-0.5" />
          <div>{uploadGlobalError}</div>
        </div>
      )}

      {uploads.length > 0 && (
        <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm">
          <div className="font-semibold text-slate-800 mb-2">
            Upload progress {activePacketId ? '(packet ' + activePacketId.slice(-6) + ')' : ''}
          </div>
          <ul className="space-y-1">
            {uploads.map((u, i) => (
              <li key={i} className="flex items-center gap-2">
                <FileText className="w-3.5 h-3.5 text-slate-400" />
                <span className="text-slate-700">Document {i + 1}</span>
                <span className={
                  u.status === 'ready'   ? 'text-emerald-700' :
                  u.status === 'failed'  ? 'text-red-700'    :
                  u.status === 'uploading' ? 'text-indigo-700' : 'text-slate-500'
                }>· {u.status}</span>
                {u.error && <span className="text-xs text-red-600">— {u.error}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="border-b border-slate-200 flex gap-1">
        <TabButton active={tab === TAB.INBOX}     onClick={() => setTab(TAB.INBOX)}>
          Inbox ({inbox.length + draftingPackets.length + failedPackets.length})
        </TabButton>
        <TabButton active={tab === TAB.COMPLETED} onClick={() => setTab(TAB.COMPLETED)}>
          Completed ({completed.length})
        </TabButton>
        <TabButton active={tab === TAB.DELETED} onClick={() => setTab(TAB.DELETED)}>
          Deleted ({deleted.length})
        </TabButton>
      </div>

      <div>
        {tab === TAB.INBOX ? inboxBody : tab === TAB.COMPLETED ? completedBody : deletedBody}
      </div>

      {canReviewDocs && reviewPacket && (
        <PacketReviewDrawer
          packet={reviewPacket}
          onClose={() => setReviewPacket(null)}
        />
      )}

      {canStartGate && preparingPacket && (
        <PreparingGateModal
          packetId={preparingPacket.packetId}
          onProceed={() => proceedToGate(preparingPacket)}
          onClose={() => setPreparingPacket(null)}
        />
      )}
    </div>
  );
};

// ── Local components ─────────────────────────────────────────────────────────

const TabButton = ({ active, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={[
      'px-4 py-2 text-sm font-medium border-b-2 -mb-px',
      active ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-slate-500 hover:text-slate-700',
    ].join(' ')}
  >
    {children}
  </button>
);

const PacketCard = ({ packet, tone, canStartGate, canReviewDocs, canDelete, canUpload, deleting, focusAnchor, onReview, onStartReferral, onContinueSmartLaunch, onAddDocuments, onReturnToInbox, onDelete }) => {
  const ready = packet.readyDocumentCount || 0;
  const total = packet.documentCount || 0;
  // Build 2H — SMART-launched packets are candidate-driven (no documents); they
  // use "Continue to Referral Gate" instead of the document-based start button.
  const sl = getSmartLaunchHandoffState(packet, { canStartGate });
  const canStart = canStartGate && ready >= 1 && tone === 'inbox' && !sl.isSmartLaunch;
  // Build 2O — EHR-launch packets may also take OPTIONAL supporting documents.
  const addDocs = canAddSupportingDocsToEhrPacket(packet, { canUpload });

  const toneClass =
    tone === 'failed'   ? 'border-red-300 bg-red-50' :
    tone === 'drafting' ? 'border-amber-300 bg-amber-50' :
                          'border-slate-200 bg-white';

  return (
    <li
      data-learning-target={focusAnchor ? 'packet_card' : undefined}
      className={'rounded-lg border p-4 flex items-center justify-between gap-4 ' + toneClass + (focusAnchor ? ' learning-focus-highlight-subtle' : '')}
    >
      <div className="min-w-0">
        {/* Human, PHI-free identity: date/time, not a random ID. */}
        <div className="text-sm font-semibold text-slate-900">Packet · {formatDateTime(packet.createdAt)}</div>
        {/* Status line: drafting copy, else scan readiness (counts/status only). */}
        {tone === 'drafting' ? (
          <div className="text-xs text-slate-600 mt-1">
            {total} {total === 1 ? 'document' : 'documents'} · referral draft in progress
          </div>
        ) : (() => {
          const scan = getPacketScanReadiness(packet);
          return (
            <div className={'text-xs mt-1 ' + (SCAN_TONE_CLASS[scan.tone] || 'text-slate-500')}>
              {scan.line}
            </div>
          );
        })()}
        {/* Build 2H — EHR-launch provenance + import-readiness (never-PHI label). */}
        {sl.isSmartLaunch && (
          <div className={'text-[11px] mt-0.5 font-medium ' + (sl.eligible ? 'text-sky-700' : 'text-slate-500')}>
            {sl.statusLabel}
          </div>
        )}
        {/* Short packet ID kept as secondary debug text (not the main label). */}
        <div className="text-[11px] text-slate-400 mt-0.5">ID: {shortPacketId(packet.packetId)}</div>
        {tone === 'failed' && packet.lastError?.message && (
          <div className="text-xs text-red-700 mt-1">Last error: {packet.lastError.message}</div>
        )}
        {tone === 'drafting' && (
          <div className="text-xs text-amber-700 mt-1">A referral is being drafted from this packet.</div>
        )}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {canReviewDocs && onReview && total > 0 && (
          <button
            type="button"
            onClick={onReview}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium border border-slate-300 rounded-md text-slate-700 hover:bg-slate-50"
            title="Open the packet to review and type its documents."
          >
            <FolderOpen className="w-3.5 h-3.5" /> Review Documents
          </button>
        )}
        {tone !== 'inbox' && onReturnToInbox && (
          <button
            type="button"
            onClick={onReturnToInbox}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium border border-slate-300 rounded-md text-slate-700 hover:bg-white"
            title="Return this packet to the Inbox so it can be retried."
          >
            <RotateCcw className="w-3.5 h-3.5" /> Return to Inbox
          </button>
        )}
        {tone === 'inbox' && !sl.isSmartLaunch && (
          <button
            type="button"
            onClick={onStartReferral}
            disabled={!canStart}
            className="px-3 py-1.5 text-xs font-medium rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:bg-slate-300 disabled:cursor-not-allowed"
            title={canStartGate
              ? (canStart ? 'Open Gate with this packet' : 'Wait for at least one document to finish uploading')
              : 'Only a referring provider can start a referral from a packet'}
          >
            Start New Referral
          </button>
        )}
        {/* Build 2O — optional supporting-document upload for EHR-launch packets
            (secondary, never replaces "Continue to Referral Gate"). */}
        {addDocs.allowed && onAddDocuments && (
          <button
            type="button"
            onClick={onAddDocuments}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium border border-sky-300 rounded-md text-sky-700 hover:bg-sky-50"
            title="Upload optional supporting documents (labs, referral letter, insurance card). Documents are optional — you can continue to the Gate without them."
          >
            <Upload className="w-3.5 h-3.5" /> Add supporting documents
          </button>
        )}
        {/* Build 2H — SMART-launch handoff into the existing Gate (candidate-driven). */}
        {sl.isSmartLaunch && (tone === 'inbox' || tone === 'drafting') && onContinueSmartLaunch && (
          <button
            type="button"
            onClick={onContinueSmartLaunch}
            disabled={!sl.eligible}
            className="px-3 py-1.5 text-xs font-medium rounded-md bg-sky-600 text-white hover:bg-sky-700 disabled:bg-slate-300 disabled:cursor-not-allowed"
            title={sl.eligible ? 'Open the Gate to review imported EHR suggestions and submit the referral' : (sl.blockMessage || 'Not ready for the Gate')}
          >
            {sl.actionLabel}
          </button>
        )}
        {canDelete && onDelete && (
          <button
            type="button"
            onClick={onDelete}
            disabled={deleting}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium border border-slate-300 rounded-md text-slate-600 hover:bg-red-50 hover:text-red-700 hover:border-red-300 disabled:opacity-50"
            title="Delete this packet from the inbox. Uploaded documents are retained on the server but removed from this packet."
          >
            {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />} Delete
          </button>
        )}
      </div>
    </li>
  );
};

const CompletedPacketCard = ({ packet }) => (
  <li className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
    <div className="text-sm font-semibold text-emerald-900">Packet · {formatDateTime(packet.createdAt)}</div>
    <div className="text-xs text-emerald-800 mt-1">
      Assigned Referral ID: <span className="font-mono">{packet.assignedReferralId || '—'}</span>
    </div>
    <div className="text-xs text-emerald-700 mt-1">
      Assigned {formatTimestamp(packet.assignedAt)} · {packet.documentCount || 0} document(s)
    </div>
    <div className="text-[11px] text-emerald-700/70 mt-0.5">ID: {shortPacketId(packet.packetId)}</div>
  </li>
);

const DeletedPacketCard = ({ packet, canRestore, restoring, onRestore }) => {
  const total = packet.documentCount || 0;
  return (
    <li className="rounded-lg border border-slate-200 bg-slate-50 p-4 flex items-center justify-between gap-4">
      <div className="min-w-0">
        <div className="text-sm font-semibold text-slate-700">Packet · {formatDateTime(packet.createdAt)}</div>
        <div className="text-xs text-slate-500 mt-1">
          {total} {total === 1 ? 'document' : 'documents'} · deleted {formatDateTime(packet.deletedAt)}
        </div>
        <div className="text-[11px] text-slate-400 mt-0.5">ID: {shortPacketId(packet.packetId)}</div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {canRestore && (
          <button
            type="button"
            onClick={onRestore}
            disabled={restoring}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium border border-slate-300 rounded-md text-slate-700 hover:bg-white disabled:opacity-50"
            title="Restore this packet to the Inbox."
          >
            {restoring ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Undo2 className="w-3.5 h-3.5" />} Restore to Inbox
          </button>
        )}
      </div>
    </li>
  );
};

export default PacketInboxView;
